import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { CheckboxProcedure } from '@/app/(main)/_components/search-modal/_components/checkbox-procedure.component';
import { Button } from '@/components/ui/button/button.component';
import { MostSearchedResult } from '@/types/most-search-type';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { appendRecentSearches, useSearchState } from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { useLoading } from '@/hooks/use-loading.hook';
import { buildSearchResult } from '@/utils/search/search-result.util';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

interface SelectedOverviewExamsProps {
  selectedProcedures: SelectedProcedures[];
  locationOverride?: OverridedUserLocation;
  handleSelectProcedure: (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => void;
  onClear?: () => void;
}

export function SelectedOverviewExams({
  selectedProcedures,
  handleSelectProcedure,
  onClear,
  locationOverride,
}: SelectedOverviewExamsProps) {
  const router = useRouter();
  const [, setStoredRecentSearches] = useRecentSearchesStorage();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { handleSearchExamsUrl } = useSearch({ locationOverride });
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch);
  const modalTitle = isVaccine ? 'Vacinas selecionadas' : 'Exames selecionados';
  const addMoreExamsButton = isVaccine ? 'Acrescentar mais vacinas' : 'Acrescentar mais exames';

  const isChecked = useCallback(
    (procedure: MostSearchedResult | SearchResult | SelectedProcedures) => {
      return selectedProcedures.some((item) => item.id === procedure.id);
    },
    [selectedProcedures],
  );

  function handleJump(key: ProgressiveSearchModalKeys, returning: boolean = false) {
    if (jump) {
      jump({ key, returning });

      if (
        key === ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE
        || key === ProgressiveSearchModalKeys.SELECT_MORE_EXAMS
      ) {
        return;
      }

      if (onClear && returning) {
        onClear();
      }
    }
  }

  const handleNavigateToScheduleResult = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    startLoading();

    try {
      const searchResult = buildSearchResult(selectedProcedures, isVaccine);

      if (searchResult) {
        appendRecentSearches(searchResult, setStoredRecentSearches);
      }

      router.push(handleSearchExamsUrl(isVaccine, selectedProcedures));
    } catch (error) {
      const logger = getLogger('SearchModal - SelectedOverviewExams');
      logger.error('Error updating latest searches', { selectedProcedures, isVaccine }, error);
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="h-full flex flex-col justify-between p-5">
      <TrackUserView eventName={AnalyticsEnum.VIEW_SELECTED_OVERVIEW_EXAMS} />

      <SheetHeader className="bg-white">
        <div className="flex justify-between gap-2.5 items-start md:items-center">
          <div className="flex gap-4 items-start md:items-center">
            <SheetTitle>
              <div className="flex gap-2.5">
                <span
                  className="font-extrabold text-2xl text-gray-900 text-wrap text-left"
                  data-testid="selected-overview-exams-title"
                >
                  {modalTitle}
                </span>
              </div>
            </SheetTitle>
          </div>
          <button
            className="text-gray-500 cursor-pointer self-start md:self-center mt-2 md:mt-0 hover:bg-gray-50"
            data-testid="back--selected-overview-exams"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump(ProgressiveSearchModalKeys.SEARCH, true);
                return;
              }

              handleJump(ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, true);
            }}
          >
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </button>
        </div>
      </SheetHeader>
      <div className="pt-3 overflow-y-scroll h-full md:basis-[40vh] -mx-5 ">
        {selectedProcedures.map((procedure) => (
          <CheckboxProcedure
            key={procedure.id || procedure.code}
            procedure={procedure}
            handleSelectProcedure={handleSelectProcedure}
            isChecked={isChecked}
          />
        ))}
      </div>
      <div className="space-y-4">
        <Button
          isLoading={isLoading}
          data-testid="see-schedules--selected-overview-exams"
          disabled={selectedProcedures.length === 0}
          onClick={handleNavigateToScheduleResult}
        >
          Ver horários
        </Button>
        <Button
          disabled={isLoading}
          variant="secondary"
          data-testid="add-more--selected-overview-exams"
          onClick={() => handleJump(ProgressiveSearchModalKeys.SELECT_MORE_EXAMS, true)}
        >
          {addMoreExamsButton}
        </Button>
      </div>
    </div>
  );
}
